'use client';

import { cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/button';
import { check as IconCheck } from '@/components/icons/check';
import { close as IconClose } from '@/components/icons/close';
import { wallet as IconWallet } from '@/components/icons/wallet';
import WalletConnectButton from '@/components/layout/header/wallet-connect/WalletConnectButton';
import ErrorWithSupport from '@/components/support/ErrorWithSupport';
import { Wallets } from '@/constants/Wallets';
import { useDucatClient } from '@/context/DucatClientCtx';
import { WalletType } from '@/context/types';
import { quantaHeadersBelongToWallet } from '@/hooks/api/quanta/quantaIdentity';
import { fetchMigrationAuthority } from '@/hooks/api/quanta/walletMigration';
import { useAppStore } from '@/stores/useAppStore';
import { cn } from '@/utils/cn';
import { copyToClipboard } from '@/utils/copyToClipboard';

import { type MigrationFlow, useQuantaMigration } from '../context/QuantaMigrationState';

const sessionIds = new WeakMap<object, number>();
const promptedSessions = new WeakMap<object, Set<string>>();
let nextSessionId = 0;
function sessionId(token?: object) {
  if (!token) return 0;
  if (!sessionIds.has(token)) sessionIds.set(token, ++nextSessionId);
  return sessionIds.get(token);
}
type Prompt = { token: object; key: string; eligible: boolean };
interface MigrationDialogProps {
  flow: MigrationFlow;
  walletType: WalletType | null;
  targetAddress?: string;
  sourceProvider?: WalletType;
  prompt?: Prompt;
  connectMetaMask: () => Promise<void>;
  loggedIn: boolean;
}

export function QuantaMigrationDialog({
  flow,
  walletType,
  targetAddress,
  sourceProvider,
  prompt,
  connectMetaMask,
  loggedIn,
}: MigrationDialogProps) {
  const t = useTranslations('quanta.migration');
  const [open, setOpen] = useState(flow.active || flow.step === 'completed');
  const [connecting, setConnecting] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);
  const promptToken = prompt?.token;
  const promptKey = prompt?.key;
  const eligible = prompt?.eligible === true;
  const busy = flow.busy || connecting;
  const reverse = flow.reverse || (flow.step === 'idle' && walletType === WalletType.Metamask);
  const selecting = reverse && (flow.step === 'idle' || flow.step === 'select-source' || (flow.step === 'preparing' && !sourceProvider));
  const completed = flow.step === 'completed';
  const linkedElsewhere = flow.step === 'linked-elsewhere';
  const verifyingOwner = flow.step === 'verify-owner' && !!flow.requiredOwner;
  const awaitingSource = flow.step === 'authenticate-source' && (flow.sourceReady === false || walletType !== sourceProvider);
  const crossNetworkRecovery = flow.step === 'recover' && flow.recovery?.source.network !== flow.recovery?.target.network;
  const ownerNetwork = t(flow.requiredOwner?.network === 'mainnet' ? 'ownerMainnet' : 'ownerTestnet');
  const rememberedProvider = useRef<WalletType | null>(sourceProvider ?? (walletType !== WalletType.Metamask ? walletType : null));
  if (sourceProvider) rememberedProvider.current = sourceProvider;
  else if (!flow.active && !completed && walletType !== WalletType.Metamask) rememberedProvider.current = walletType;
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a new idle account/session resets dialog visibility; target switching preserves the active flow.
  useEffect(() => {
    if (!flow.active && !completed) setOpen(false);
  }, [promptToken, promptKey]);
  useEffect(() => {
    if (!promptToken || !promptKey || !eligible || flow.active || flow.step === 'completed') return;
    const prompted = promptedSessions.get(promptToken) ?? new Set<string>();
    if (prompted.has(promptKey)) return;
    prompted.add(promptKey);
    promptedSessions.set(promptToken, prompted);
    setOpen(true);
  }, [promptToken, promptKey, eligible, flow.active, flow.step]);
  useEffect(() => {
    // Recovery is restored asynchronously by the provider; keep the saved flow accessible.
    if (flow.active || completed) setOpen(true);
  }, [flow.active, completed]);
  useEffect(() => {
    if (!flow.active && flow.step !== 'completed' && !loggedIn) setOpen(false);
  }, [loggedIn, flow.active, flow.step]);

  const provider = sourceProvider ?? rememberedProvider.current;
  const source = provider ? Wallets[provider] : { label: t('originalWallet'), logo: <IconWallet className="size-8" /> };
  const ready = flow.step === 'connect' && walletType === WalletType.Metamask && !!targetAddress;
  const activeStep =
    flow.step === 'approving-target' ? 2 : flow.step === 'submitting' || completed ? 3 : ready || flow.step === 'approving-source' ? 1 : 0;
  const errorKeys: Record<string, string> = {
    MIGRATION_SOURCE_NOT_REGISTERED: 'sourceNotRegistered',
    MIGRATION_CONNECTION_FAILED: 'connectionFailed',
    MIGRATION_TARGET_CHANGED: 'targetChanged',
    MIGRATION_PENDING: 'pending',
    MIGRATION_EXPIRED: 'expired',
    MIGRATION_WALLET_CHANGED: 'walletChanged',
    MIGRATION_SOURCE_CHANGED: 'sourceChanged',
    MIGRATION_LOGIN_REQUIRED: 'loginRequired',
    MIGRATION_RECOVERY_WALLET_REQUIRED: 'recoveryWalletRequired',
    MIGRATION_AUTHORITY_REQUIRED: 'authorityRequired',
    MIGRATION_OWNER_WALLET_REQUIRED: 'ownerWalletRequired',
    MIGRATION_OWNER_NETWORK_REQUIRED: 'ownerNetworkRequired',
    ACCOUNT_AUTHORITY_REQUIRED: 'authorityRequired',
    MIGRATION_TARGET_INVALID: 'targetInvalid',
    MIGRATION_TARGET_REQUIRED: 'targetRequired',
    MIGRATION_TARGET_CONFLICT: 'targetConflict',
    MIGRATION_TARGET_IN_USE: 'targetConflict',
    MIGRATION_TARGET_REGISTERED: 'targetConflict',
    MIGRATION_REVISION_CHANGED: 'revisionChanged',
    MIGRATION_UNAVAILABLE: 'unavailable',
  };
  const changeOpen = (next: boolean) => {
    if (!busy) setOpen(next);
  };
  const connect = useCallback(async () => {
    if (busy) return;
    setConnecting(true);
    setConnectionFailed(false);
    try {
      await connectMetaMask();
    } catch {
      setConnectionFailed(true);
    } finally {
      setConnecting(false);
    }
  }, [busy, connectMetaMask]);
  const preparingSource = useRef(false);
  useEffect(() => {
    if (
      flow.step !== 'authenticate-source' ||
      flow.busy ||
      flow.sourceReady === false ||
      walletType !== sourceProvider ||
      preparingSource.current
    )
      return;
    preparingSource.current = true;
    void flow
      .begin()
      .then(async (prepared) => {
        if (prepared) await connect();
      })
      .finally(() => {
        preparingSource.current = false;
      });
  }, [flow.step, flow.busy, flow.sourceReady, flow.begin, walletType, sourceProvider, connect]);
  const pendingStatus = connecting
    ? 'connecting'
    : flow.step === 'approving-source'
      ? 'approveSource'
      : flow.step === 'approving-target'
        ? 'approveTarget'
        : flow.step === 'submitting'
          ? 'submitting'
          : 'working';
  if (!flow.active && flow.step !== 'completed' && (!loggedIn || (walletType === WalletType.Metamask && !flow.canCombine))) return null;
  return (
    <Dialog.Root open={open} onOpenChange={changeOpen}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-text-secondary">
        <span>{t(flow.addressesMatch ? 'addressesMatchTitle' : completed ? 'completedTitle' : reverse ? 'reverseTitle' : 'title')}</span>
        <Dialog.Trigger asChild>
          <Button size="small" variant="tertiary">
            {t(completed ? 'viewResult' : flow.active ? 'resume' : reverse ? 'reverseBegin' : 'begin')}
          </Button>
        </Dialog.Trigger>
      </div>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 animate-fade-in" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[440px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-border-primary bg-surface-primary p-6 text-text-primary shadow-xl focus:outline-none sm:p-8"
          aria-busy={busy}
          onEscapeKeyDown={(event) => busy && event.preventDefault()}
          onPointerDownOutside={(event) => busy && event.preventDefault()}
        >
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm font-medium">
              {selecting || flow.addressesMatch ? (
                <>
                  {cloneElement(Wallets[WalletType.Metamask].logo, { className: 'size-8', width: 32, height: 32 })}
                  <span>MetaMask</span>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1.5">
                    {cloneElement(source.logo, { className: 'size-8', width: 32, height: 32 })}
                    <span>{source.label}</span>
                  </div>
                  <span aria-hidden="true" className="text-text-secondary">
                    →
                  </span>
                  <div className="flex items-center gap-1.5">
                    {cloneElement(Wallets[WalletType.Metamask].logo, { className: 'size-8', width: 32, height: 32 })}
                    <span>MetaMask</span>
                  </div>
                </>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={busy}
                aria-label={t('close')}
                className="shrink-0 rounded-md p-2 text-text-secondary hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-40"
              >
                <IconClose className="size-4" />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Title className="text-xl font-medium leading-7 sm:text-2xl">
            {t(
              verifyingOwner
                ? 'ownerTitle'
                : flow.addressesMatch
                  ? 'addressesMatchTitle'
                  : linkedElsewhere
                    ? 'linkedElsewhereTitle'
                    : flow.alreadyLinked
                      ? 'alreadyLinkedTitle'
                      : completed
                        ? 'completedTitle'
                        : reverse
                          ? 'reverseTitle'
                          : 'dialogTitle',
            )}
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm leading-6 text-text-secondary">
            {t(
              verifyingOwner
                ? 'ownerDescription'
                : flow.addressesMatch
                  ? 'addressesMatchDescription'
                  : linkedElsewhere
                    ? 'linkedElsewhereDescription'
                    : completed
                      ? 'completed'
                      : selecting
                        ? 'reverseDescription'
                        : 'dialogDescription',
              { wallet: source.label },
            )}
          </Dialog.Description>
          {!completed && (
            <details className="my-4 text-sm leading-6 text-text-secondary">
              <summary className="cursor-pointer">{t('browserHelpTitle')}</summary>
              <p className="mt-2">{t('browserHelp')}</p>
            </details>
          )}
          {verifyingOwner && flow.requiredOwner && (
            <div className="my-5 space-y-4 text-sm leading-6">
              <div className="rounded-xl border border-border-primary bg-surface-secondary p-4">
                <p className="text-text-secondary">{t('ownerAddress', { network: ownerNetwork })}</p>
                <p className="mt-2 break-all font-mono text-text-primary">{flow.requiredOwner.address}</p>
                <Button
                  size="small"
                  variant="tertiary"
                  className="mt-2"
                  disabled={busy}
                  onClick={() => void copyToClipboard(flow.requiredOwner?.address ?? '', t('ownerCopied'))}
                >
                  {t('ownerCopy')}
                </Button>
              </div>
              <ol className="list-decimal space-y-2 pl-5" aria-label={t('ownerSteps')}>
                <li>{t('ownerConnectTarget')}</li>
                <li>{t('ownerSelect', { network: ownerNetwork })}</li>
                <li>{t('ownerApprove')}</li>
              </ol>
              <p className="text-text-secondary">{t('ownerNetworkHelp', { network: ownerNetwork })}</p>
            </div>
          )}
          {linkedElsewhere && (
            <div className="mt-5 rounded-xl border border-border-primary bg-surface-secondary p-4 text-sm">
              <p className="mb-2 text-text-secondary">{t('linkedMetaMask')}</p>
              <p className="break-all font-mono">{flow.existingLink}</p>
            </div>
          )}
          {!completed && !selecting && !linkedElsewhere && !verifyingOwner && (
            <ol aria-label={t('steps')} className="my-6 space-y-4">
              {(['connectStep', 'sourceStep', 'targetStep'] as const).map((label, index) => (
                <li
                  key={label}
                  aria-current={activeStep === index ? 'step' : undefined}
                  className="flex items-center gap-3 text-sm leading-5"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium',
                      activeStep > index
                        ? 'border-text-primary bg-text-primary text-surface-primary'
                        : activeStep === index
                          ? 'border-text-primary text-text-primary'
                          : 'border-border-primary text-text-secondary',
                    )}
                  >
                    {activeStep > index ? <IconCheck className="size-4" /> : index + 1}
                  </span>
                  <span className={activeStep >= index ? 'text-text-primary' : 'text-text-secondary'}>
                    {t(label, { wallet: source.label })}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {!verifyingOwner && (flow.sourceAddress || flow.targetAddress || ready) && (
            <details className="mb-4 text-xs text-text-secondary">
              <summary className="cursor-pointer py-1">{t('walletDetails')}</summary>
              <dl className="mt-2 space-y-2 leading-5">
                {flow.sourceAddress && (
                  <div>
                    <dt>{t('sourceWallet')}</dt>
                    <dd className="break-all font-mono text-text-primary">{flow.sourceAddress}</dd>
                  </div>
                )}
                {(flow.targetAddress || ready) && (
                  <div>
                    <dt>{t('targetWallet')}</dt>
                    <dd className="break-all font-mono text-text-primary">{flow.targetAddress ?? targetAddress}</dd>
                  </div>
                )}
              </dl>
            </details>
          )}
          <div aria-live="polite" className="space-y-3 text-sm leading-6">
            {flow.error && (
              <ErrorWithSupport as="p" role="alert" className="text-text-primary">
                {t(
                  verifyingOwner && !errorKeys[flow.error]
                    ? 'ownerRetry'
                    : (errorKeys[flow.error] ?? (flow.recovery ? 'failed' : 'preparationFailed')),
                )}
              </ErrorWithSupport>
            )}
            {connectionFailed && (
              <ErrorWithSupport as="p" role="alert" className="text-text-primary">
                {t('connectionFailed')}
              </ErrorWithSupport>
            )}
            {flow.error === 'MIGRATION_SOURCE_NOT_REGISTERED' && !!flow.checkedSourceAddresses?.length && (
              <div className="rounded-xl border border-border-primary p-3">
                <p>{t('checkedSourceAddresses')}</p>
                <ul className="mt-2 space-y-2 break-all font-mono text-xs">
                  {flow.checkedSourceAddresses.map((address) => (
                    <li key={address}>{address}</li>
                  ))}
                </ul>
                <p className="mt-2 text-text-secondary">{t('sourceAccountHelp')}</p>
              </div>
            )}
            {busy && (
              <p role="status" className="text-text-secondary">
                {t(pendingStatus)}
              </p>
            )}
            {!busy && awaitingSource && (
              <p role="status" className="text-text-secondary">
                {t('preparingSource')}
              </p>
            )}
            {flow.step === 'recover' && <p className="text-text-secondary">{t('recoverDescription')}</p>}
            {crossNetworkRecovery && flow.recovery && (
              <p className="text-text-secondary">
                {t('ownerRecovery', { network: t(flow.recovery.source.network === 'mainnet' ? 'ownerMainnet' : 'ownerTestnet') })}
                <span className="mt-2 block break-all font-mono text-text-primary">{flow.recovery.source.address}</span>
              </p>
            )}
            {!flow.available && !flow.availabilityLoading && flow.step === 'idle' && (
              <p role="status" className="text-text-secondary">
                {t('unavailable')}
              </p>
            )}
          </div>
          {!completed && (
            <p className="mt-5 text-xs leading-5 text-text-secondary">
              {t(linkedElsewhere ? 'linkedElsewhereHelp' : 'noFundsTransferred')}
            </p>
          )}
          <div className="mt-6 flex flex-col gap-2">
            {verifyingOwner &&
              (walletType !== WalletType.Metamask ? (
                <Button className="w-full" disabled={busy} loading={connecting} onClick={() => void connect()}>
                  {t('connectStep')}
                </Button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {[WalletType.Xverse, WalletType.Unisat].map((ownerProvider) => (
                    <Button
                      key={ownerProvider}
                      className="w-full"
                      variant={ownerProvider === provider ? 'primary' : 'secondary'}
                      disabled={busy}
                      onClick={() => void flow.verifyOwner?.(ownerProvider)}
                    >
                      {t('ownerVerify', { wallet: Wallets[ownerProvider].label })}
                    </Button>
                  ))}
                </div>
              ))}
            {linkedElsewhere && (
              <Button className="w-full" disabled={busy} onClick={() => flow.chooseAnotherSource?.()}>
                {t('chooseAnotherWallet')}
              </Button>
            )}
            {selecting && (
              <div className="grid grid-cols-2 gap-3">
                {[WalletType.Unisat, WalletType.Xverse].map((provider) => (
                  <button
                    key={provider}
                    type="button"
                    disabled={busy || !flow.available}
                    className="flex flex-col items-center gap-3 rounded-xl border border-border-primary p-5 text-sm hover:bg-surface-secondary focus-visible:outline focus-visible:outline-2 disabled:opacity-50"
                    onClick={() => {
                      void (async () => {
                        if (flow.step === 'idle' && !(await flow.begin())) return;
                        await flow.connectSource?.(provider);
                      })();
                    }}
                  >
                    {cloneElement(Wallets[provider].logo, { className: 'size-10', width: 40, height: 40 })}
                    {Wallets[provider].label}
                  </button>
                ))}
              </div>
            )}
            {flow.step === 'idle' && !selecting && (
              <Button
                className="w-full"
                disabled={busy || flow.availabilityLoading}
                onClick={() => {
                  if (!flow.available) {
                    flow.retryAvailability();
                    return;
                  }
                  void flow.begin().then((prepared) => {
                    if (prepared) return connect();
                  });
                }}
              >
                {t(flow.available ? 'connectStep' : 'checkAvailability')}
              </Button>
            )}
            {flow.step === 'connect' && !ready && (
              <Button className="w-full" disabled={busy} loading={connecting} onClick={() => void connect()}>
                {t('connectStep')}
              </Button>
            )}
            {ready && (
              <Button className="w-full" disabled={busy} onClick={() => void flow.approve()}>
                {t('approveBoth')}
              </Button>
            )}
            {flow.step === 'recover' && (
              <>
                {crossNetworkRecovery ? (
                  walletType !== WalletType.Metamask && (
                    <Button className="w-full" disabled={busy} onClick={() => void connect()}>
                      {t('connectStep')}
                    </Button>
                  )
                ) : (
                  <WalletConnectButton purpose="quanta-login" mobileStylesOn={false} />
                )}
                {crossNetworkRecovery &&
                  walletType === WalletType.Metamask &&
                  [WalletType.Xverse, WalletType.Unisat].map((ownerProvider) => (
                    <Button
                      key={ownerProvider}
                      variant="secondary"
                      className="w-full"
                      disabled={busy}
                      onClick={() => void flow.recoverWithOwner?.(ownerProvider)}
                    >
                      {t('ownerRecoverWith', { wallet: Wallets[ownerProvider].label })}
                    </Button>
                  ))}
                <Button className="w-full" disabled={busy || !walletType} onClick={() => void flow.recover()}>
                  {t('checkStatus')}
                </Button>
              </>
            )}
            {flow.active && (
              <Button variant="tertiary" className="w-full" disabled={busy} onClick={() => void flow.cancel()}>
                {t(linkedElsewhere ? 'done' : 'cancel')}
              </Button>
            )}
            {completed ? (
              <Button
                className="w-full"
                onClick={() => {
                  setOpen(false);
                  flow.dismiss();
                }}
              >
                {t(flow.addressesMatch ? 'continueToQuanta' : 'done')}
              </Button>
            ) : (
              !flow.active && (
                <Dialog.Close asChild>
                  <Button variant="tertiary" className="w-full" disabled={busy}>
                    {t('later')}
                  </Button>
                </Dialog.Close>
              )
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function QuantaMigrationPanel({
  loggedIn,
  userId,
  authenticationPending = false,
}: {
  loggedIn: boolean;
  userId?: string;
  authenticationPending?: boolean;
}) {
  const { wallet, walletType, accountRevision, client } = useDucatClient();
  const headers = useAppStore((s) => s.quantaHeaders);
  const flow = useQuantaMigration();
  const validSession = loggedIn && !!userId && quantaHeadersBelongToWallet(headers, wallet, accountRevision);
  const authority = useQuery({
    queryKey: [
      'quanta-migration-prompt',
      userId,
      walletType,
      headers?.['x-address'],
      headers?.['x-user-public-key'],
      accountRevision,
      sessionId(headers),
    ],
    enabled:
      !!flow?.available &&
      !flow.active &&
      flow.step === 'idle' &&
      validSession &&
      [WalletType.Xverse, WalletType.Unisat, WalletType.Metamask].some((provider) => provider === walletType) &&
      !authenticationPending,
    retry: false,
    staleTime: 60_000,
    queryFn: () => {
      if (!headers) throw new Error('MIGRATION_LOGIN_REQUIRED');
      return fetchMigrationAuthority(headers);
    },
  });
  if (!flow) return null;
  if (
    !flow.active &&
    flow.step === 'idle' &&
    validSession &&
    authority.data?.userId === userId &&
    authority.data.migrated &&
    walletType !== WalletType.Metamask
  )
    return null;
  const prompt =
    headers && validSession
      ? {
          token: headers,
          key: JSON.stringify([userId, walletType, headers['x-address'], headers['x-user-public-key']]),
          eligible:
            [WalletType.Xverse, WalletType.Unisat, WalletType.Metamask].some((provider) => provider === walletType) &&
            flow.available &&
            !authenticationPending &&
            !authority.isFetching &&
            !authority.isError &&
            authority.data?.userId === userId &&
            authority.data?.migrated === false,
        }
      : undefined;
  return (
    <QuantaMigrationDialog
      flow={flow}
      walletType={walletType}
      sourceProvider={flow.sourceProvider}
      targetAddress={wallet?.account.vault.address}
      prompt={prompt}
      loggedIn={loggedIn}
      connectMetaMask={async () => {
        if (!(await client.connect(WalletType.Metamask))) throw new Error('MetaMask connection was not completed');
      }}
    />
  );
}
